/**
 * The zero-row state for index grids — the `docs/STYLES.md` "Empty States"
 * shape (icon at `h-12 w-12`, heading, explanation, optional action) rendered
 * INSIDE the table body, in a full-width row that replaces the body rows.
 *
 * Two cases, deliberately different copy:
 *   - **Nothing at all** (`filtered: false`) — the list is genuinely empty, so
 *     the copy points at creating the first record.
 *   - **Search / filters matched nothing** (`filtered: true`) — data exists but
 *     the current query hid it, so the copy points at loosening the query.
 *
 * `useIndexGridShell` decides which case applies (it owns the search term and
 * knows the caller's raw row count) and wires this through the primitive's
 * `renderEmptyState`. The header row is deliberately still rendered above it —
 * a user who filtered themselves into nothing must be able to reach the column
 * filter that did it.
 *
 * Mirrors the legacy `entities-data-grid/EmptyState` markup rather than
 * importing it: that module belongs to the deprecated `EntitiesDataGrid` (see
 * `docs/INDEX_GRID_PATTERN.md`) and is due for deletion, and its API carries a
 * `searchTerm` string instead of the two-case split.
 */
import type { ReactNode } from 'react';
import { Inbox, SearchX, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/hooks/useTranslation';

export interface IndexGridEmptyStateProps {
  /**
   * `true` when rows exist but the search term / column filters matched none
   * of them; `false` when there is genuinely no data.
   */
  filtered: boolean;
  /** Icon override. Defaults: `Inbox` (no data) / `SearchX` (no matches). */
  icon?: LucideIcon;
  /** Heading override. Defaults to the translated copy for the case. */
  title?: ReactNode;
  /** Explanation override. Defaults to the translated copy for the case. */
  description?: ReactNode;
  /**
   * Optional create affordance (typically a `<Button>`). Rendered only in the
   * no-data case — offering "create one" to someone whose *filter* matched
   * nothing answers a question they didn't ask.
   */
  action?: ReactNode;
}

export function IndexGridEmptyState({
  filtered,
  icon,
  title,
  description,
  action,
}: IndexGridEmptyStateProps) {
  const { t } = useTranslation('spreadsheet');

  const Icon = icon ?? (filtered ? SearchX : Inbox);
  const heading =
    title ?? (filtered ? t('emptyState.noMatchesTitle') : t('emptyState.noRecordsTitle'));
  const body =
    description
    ?? (filtered ? t('emptyState.noMatchesDescription') : t('emptyState.noRecordsDescription'));
  const showAction = !filtered && !!action;

  return (
    <div className="text-center py-8">
      <Icon className="h-12 w-12 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
      <h3 className="text-lg font-semibold mb-2">{heading}</h3>
      <p className={cn('text-muted-foreground max-w-md mx-auto', showAction && 'mb-4')}>{body}</p>
      {showAction ? action : null}
    </div>
  );
}
